import { Product, Order, ProductAssignment } from '../types';
import axios from 'axios';
import { baseUrl } from '../utils/config';

interface QuestionData {
  questions: Array<{
    id: string;
    question: string;
    options: Array<{
      id: string;
      text: string;
      nextQuestion?: string;
      recommendations?: string[];
    }>;
  }>;
}
interface SynthesisSection {
  what_we_found: string;
  why_this_matters: string;
  root_causes: string;
  skin_analysis: string;
  nutrition_insights: string;
  wellbeing_profile: string;
  healing_plan: string;
  next_steps: string;
  brief_resume: string;
  full_text: string;
}

interface SynthesisResponse {
  synthesis: SynthesisSection | { error: string; details: string };
}

interface ProductData {
  products: Array<{
    id: string;
    name: string;
    description: string;
    price: number;
    imageData?: string;
    imageMimeType?: string;
    category?: string;
    inStock: boolean;
  }>;
}

interface Diagnosis {
  id: string;
  name: string;
  description: string;
  imageData?: string;
  imageMimeType?: string;
}

interface DatabaseOption {
  id?: string;
  text: string;
  nextQuestion?: string;
  recommendations?: string[];
}

interface DatabaseQuestion {
  id: string;
  question: string;
  options: DatabaseOption[];
}

interface QuestionResponse {
  questions: DatabaseQuestion[];
}

const token = localStorage.getItem('token'); // or wherever you're storing the token


// ============================================================================

// NEW TREE-AWARE INTERFACES (Multi-Tree Functionality)

//These interfaces support the new tree functionality

// ============================================================================



interface QuestionTree {

  id: string;

  name: string;

  field_name: string;

  description?: string;

  estimated_duration?: number;

  display_order: number;

  question_count: number;

  is_active: boolean;

  created_at?: string;

}



interface TreeSequence {

  id: string;

  name: string;

  description?: string;

  tree_ids: string[];

  is_default: boolean;

  is_active: boolean;

  created_at?: string;

}



interface TreeSequenceWithDetails {

  sequence: TreeSequence;

  trees: QuestionTree[];

}



interface TreeQuestionResponse {

  tree: {

    id: string;

    name: string;

    field_name: string;

    description?: string;

  };

  questions: DatabaseQuestion[];

}



// Update the API base URL to use centralized config

const API_BASE_URL = baseUrl;


class DatabaseService {
  private api;
  private initialized: boolean = false;

  constructor() {
    this.api = axios.create({
      baseURL: baseUrl,
      headers: {
        'Content-Type': 'application/json',
      },
    });
    this.api.interceptors.request.use((config) => {
      const token = localStorage.getItem('auth_token');
      if (token) {
        config.headers = config.headers || {};
        (config.headers as any).Authorization = `Bearer ${token}`;
      }
      return config;
    });
    this.initializeAsync();
  }

  async finalSynthesis(
    diagnoses: any[], 
    demographics?: { ageGroup: string; exactAge?: number; gender: string }
  ): Promise<SynthesisSection> {
  try {
    const payload: any = { diagnoses };
    
    // Add demographics if provided for personalized synthesis
    if (demographics) {
      payload.age_group = demographics.ageGroup;
      payload.exact_age = demographics.exactAge;
      payload.gender = demographics.gender;
      console.log('📊 Sending demographics to synthesis:', demographics);
    }
    
    const response = await this.api.post<SynthesisResponse>('/final_synthesis', payload);
    
    const synthesis = response.data.synthesis;
    
    // Some backends may return an error-shaped object; construct a safe fallback synthesis
    if (!synthesis || typeof synthesis !== 'object' || 'error' in (synthesis as any)) {
      const names: string[] = (diagnoses || []).map((d: any) => d?.name || 'Condition');
      const descriptions: string[] = (diagnoses || []).map((d: any) => d?.description || '');
      const bullets = names.map((n, i) => `- ${n}: ${descriptions[i]?.substring(0, 180) || ''}`).join('\n');
      const fallback: SynthesisSection = {
        what_we_found: `We identified the following key points based on your inputs:\n${bullets}`,
        why_this_matters: 'Understanding these aspects helps us tailor a practical plan for your skin, nutrition, and overall wellbeing.',
        root_causes: 'Your profile suggests a combination of lifestyle and individual factors. We will address hydration, balanced nutrition, and stress/sleep patterns.',
        skin_analysis: 'Maintain a gentle routine: cleanse, hydrate, protect with SPF. Avoid harsh actives; add targeted care only as needed.',
        nutrition_insights: 'Focus on whole foods, steady hydration, reduce ultra-processed foods and added sugars. Favor colorful vegetables, lean proteins, and healthy fats.',
        wellbeing_profile: 'Prioritize consistent sleep routines and simple stress relievers (short walks, breathing, journaling). Small steady habits matter most.',
        healing_plan: '1) Daily SPF + moisturizer. 2) Add nutrient-dense meals and water target. 3) 10–15 min movement and wind‑down each day. Track progress weekly.',
        next_steps: 'If symptoms persist or worsen, consult a dermatologist or nutrition professional. Bring this summary to your appointment.',
        brief_resume: 'Balanced, supportive plan across skin, body, and mind—focused on gentle care, smart nutrition, and steady wellbeing habits.',
        full_text: ''
      };
      return fallback;
    }

    return synthesis as SynthesisSection;
  } catch (error) {
    console.error('Error fetching final synthesis:', error);
    // Client-side fallback in case of network/exception
    const names: string[] = (diagnoses || []).map((d: any) => d?.name || 'Condition');
    const descriptions: string[] = (diagnoses || []).map((d: any) => d?.description || '');
    const bullets = names.map((n, i) => `- ${n}: ${descriptions[i]?.substring(0, 180) || ''}`).join('\n');
    return {
      what_we_found: `We identified the following key points based on your inputs:\n${bullets}`,
      why_this_matters: 'Understanding these aspects helps us tailor a practical plan for your skin, nutrition, and overall wellbeing.',
      root_causes: 'Your profile suggests a combination of lifestyle and individual factors. We will address hydration, balanced nutrition, and stress/sleep patterns.',
      skin_analysis: 'Maintain a gentle routine: cleanse, hydrate, protect with SPF. Avoid harsh actives; add targeted care only as needed.',
      nutrition_insights: 'Focus on whole foods, steady hydration, reduce ultra-processed foods and added sugars. Favor colorful vegetables, lean proteins, and healthy fats.',
      wellbeing_profile: 'Prioritize consistent sleep routines and simple stress relievers (short walks, breathing, journaling). Small steady habits matter most.',
      healing_plan: '1) Daily SPF + moisturizer. 2) Add nutrient-dense meals and water target. 3) 10–15 min movement and wind‑down each day. Track progress weekly.',
      next_steps: 'If symptoms persist or worsen, consult a dermatologist or nutrition professional. Bring this summary to your appointment.',
      brief_resume: 'Balanced, supportive plan across skin, body, and mind—focused on gentle care, smart nutrition, and steady wellbeing habits.',
      full_text: ''
    } as SynthesisSection;
  }
}

  private async initializeAsync() {
    try {
      console.log('Initializing DatabaseService, connecting to API at:', baseUrl);
      const response = await this.api.get('/questions');
      console.log('API connection successful:', response.status);
      this.initialized = true;
    } catch (error: any) {
      console.error('Failed to connect to API:', error);
      // Try to provide more detailed error information
      if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        console.error('Error response data:', error.response.data);
        console.error('Error response status:', error.response.status);
        console.error('Error response headers:', error.response.headers);
      } else if (error.request) {
        // The request was made but no response was received
        console.error('No response received from server. Request:', error.request);
      } else {
        // Something happened in setting up the request that triggered an Error
        console.error('Error setting up request:', error.message);
      }
    }
  }

  async getQuestions(): Promise<QuestionData> {
    try {
      const { data } = await this.api.get('/questions');
      console.log('Raw data from questions.php:', data);

      // Ensure we return the expected structure
      if (data && typeof data === 'object') {
        if (data.questions && Array.isArray(data.questions)) {
          console.log('Found questions array in response:', data.questions.length);
          return data as QuestionData;
        } else if (Array.isArray(data)) {
          console.log('Response is an array, wrapping in questions object:', data.length);
          return { questions: data };
        } else {
          console.error('Unexpected data structure:', data);
          // If we received the JSON string you provided, try to parse it
          if (typeof data === 'string' && data.trim().startsWith('{')) {
            try {
              const parsedData = JSON.parse(data);
              if (parsedData.questions && Array.isArray(parsedData.questions)) {
                console.log('Parsed questions from string:', parsedData.questions.length);
                return parsedData as QuestionData;
              }
            } catch (parseError) {
              console.error('Error parsing data as JSON:', parseError);
            }
          }
          return { questions: [] };
        }
      } else if (typeof data === 'string') {
        // Try to parse the string as JSON
        try {
          const parsedData = JSON.parse(data);
          if (parsedData.questions && Array.isArray(parsedData.questions)) {
            console.log('Parsed questions from string:', parsedData.questions.length);
            return parsedData as QuestionData;
          } else {
            console.error('Parsed data does not have questions array:', parsedData);
            return { questions: [] };
          }
        } catch (parseError) {
          console.error('Error parsing data as JSON:', parseError);
          return { questions: [] };
        }
      } else {
        console.error('Invalid data received:', data);
        return { questions: [] };
      }
    } catch (error) {
      console.error('Error fetching questions:', error);
      return { questions: [] };
    }
  }

  async saveQuestions(data: any): Promise<void> {
    try {
      console.log('Raw data received:', data);

      // Normalize the data structure
      const questions = data.questions?.questions || data.questions || (Array.isArray(data) ? data : []);

      console.log('Normalized questions array:', questions);

      if (!Array.isArray(questions)) {
        throw new Error('Questions must be an array');
      }

      // First pass: Collect all question IDs and validate basic structure
      const questionIds = new Set(questions.map(q => q.id));
      const missingQuestionIds = new Set<string>();

      // Validate each question and collect missing next_question_ids
      questions.forEach((q, index) => {
        if (!q.id || !q.question || !Array.isArray(q.options)) {
          throw new Error(`Invalid question format at index ${index}`);
        }
        // Validate ID length
        if (q.id.length > 50) {
          throw new Error(`Question ID at index ${index} exceeds 50 characters`);
        }
        q.options.forEach((opt: any, optIndex: number) => {
          if (!opt.text) {
            throw new Error(`Invalid option format at question ${index}, option ${optIndex}`);
          }
          // Collect missing next_question_ids
          if (opt.nextQuestion && !questionIds.has(opt.nextQuestion)) {
            missingQuestionIds.add(opt.nextQuestion);
          }
        });
      });

      // Create placeholder questions for missing next_question_ids
      const placeholderQuestions = Array.from(missingQuestionIds).map(id => ({
        id,
        question: `Placeholder question for ${id}`,
        options: [{
          text: 'Default Option',
          recommendations: []
        }]
      }));

      // Combine original and placeholder questions
      const allQuestions = [...questions, ...placeholderQuestions];

      // Ensure default diagnosis exists
      await this.ensureDefaultDiagnosis();

      // Format data for the PHP endpoint
      const formattedData = {
        questions: allQuestions.map((q: any) => ({
          id: q.id,
          question: q.question,
          options: q.options.map((opt: any, index: number) => ({
            id: `${q.id}_opt_${index + 1}`.substring(0, 50), // Ensure ID fits VARCHAR(50)
            question_id: q.id,
            text: opt.text,
            nextQuestion: opt.nextQuestion || null  // Changed to match backend
          }))
        }))
      };

      // Add recommendations separately
      const recommendations: any[] = [];
      allQuestions.forEach((q: any) => {
        q.options.forEach((opt: any, index: number) => {
          if (opt.recommendations && opt.recommendations.length > 0) {
            opt.recommendations.forEach((rec: string) => {
              // Ensure product_id fits VARCHAR(50)
              const product_id = rec.substring(0, 50);
              recommendations.push({
                option_id: `${q.id}_opt_${index + 1}`.substring(0, 50),
                product_id,
                diagnosis_id: 'default_diagnosis'
              });
            });
          }
        });
      });

      const finalData = {
        questions: formattedData.questions,
        recommendations
      };

      console.log('Sending formatted data to server:', finalData);

      // First, ensure all referenced products exist
      const uniqueProductIds = new Set(recommendations.map(r => r.product_id));
      const products = await this.getProducts();
      const existingProductIds = new Set(products.map(p => p.id));

      // Create missing products with placeholder data
      const now = new Date().toISOString();
      for (const productId of uniqueProductIds) {
        if (!existingProductIds.has(productId)) {
          await this.saveProduct({
            id: productId,
            name: productId,
            description: 'Automatically created product',
            price: 0,
            inStock: true,
            createdAt: now,
            updatedAt: now
          });
        }
      }

      await this.api.post('/questions', finalData, {
        headers: {
          'Content-Type': 'application/json'
        }
      });
    } catch (error) {
      console.error('Error saving questions:', error);
      throw error;
    }
  }

  async deleteAllQuestions(): Promise<void> {
    try {
      // First delete all option recommendations (they reference question_options)
      await this.api.post('/questions', { action: 'delete_all' }, {
        headers: {
          'Content-Type': 'application/json'
        }
      });
    } catch (error) {
      console.error('Error deleting questions:', error);
      if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as { response: { data: any } };
        console.error('Server response:', axiosError.response.data);
      }
      throw error;
    }
  }

  async deleteQuestion(questionId: string): Promise<void> {
    try {
      await this.api.post('/questions', {
        action: 'delete',
        questionId
      }, {
        headers: {
          'Content-Type': 'application/json'
        }
      });
    } catch (error) {
      console.error('Error deleting question:', error);
      throw error;
    }
  }

  // Products Methods
  async getProducts(country?: string): Promise<Product[]> {
    try {
      let url = '/products?limit=20'; // keep default limit
      if (country) {
        url += `&country=${encodeURIComponent(country)}`;
      }
      const { data } = await this.api.get(url);
      // Ensure each product has the instructions field, even if null/empty
      const allProducts = (data.products || []).map((product: any) => ({
        ...product,
        instructions: product.instructions || '' // Add instructions field if missing
      })) as Product[];
      return allProducts;
    } catch (error) {
      console.error('Error fetching products:', error);
      return [];
    }
  }

  async getProductsPaged(skip = 0, limit = 20, country?: string): Promise<{ products: Product[]; total?: number; skip: number; limit: number }> {
    try {
      let url = `/products?skip=${skip}&limit=${limit}`;
      if (country) {
        url += `&country=${encodeURIComponent(country)}`;
      }
      const { data } = await this.api.get(url);
      const products = (data.products || []).map((product: any) => ({
        ...product,
        instructions: product.instructions || ''
      })) as Product[];
      return {
        products,
        total: data.total,
        skip: data.skip ?? skip,
        limit: data.limit ?? limit
      };
    } catch (error) {
      console.error('Error fetching paged products:', error);
      return { products: [], total: 0, skip, limit };
    }
  }

  async saveProduct(product: Product): Promise<void> {
  try {
    const payload = {
      id: product.id,
      id2: product.id2 || product.id,
      name: product.name,
      description: product.description,
      price: product.price,
      // Backend requires category_id (non-null). Fallback to provided fields or a default bucket
      category_id: product.category_id || (product.category as string) || 'uncategorized',
      category: product.category || '',
      inStock: (product.numberInStock || 0) > 0,
      stock_number: product.numberInStock || 0, // Include stock number field (backend expects stock_number)
      imageData: product.imageData || null,         // base64 string
      imageMimeType: product.imageMimeType || null, // MIME type like 'image/jpeg'
      is_verified_ai: product.is_verified_ai || false,
      is_approved_admin: product.is_approved_admin || false,
      status: product.status || 'pending',
      composant_chimique: product.composant_chimique || null, // Include composant_chimique field
      instructions: product.instructions || '', // Include instructions field
      //partner_id: product.partner_id || null,// Assuming partner_id is optional
    };

    await this.api.post('/products', payload);
  } catch (error) {
    console.error('Error saving product:', error);
    throw error;
  }
}


  async deleteProduct(productId: string): Promise<void> {
    try {
      if (!productId) {
        throw new Error('Product ID is required for deletion');
      }

      console.log(`Attempting to delete product with ID: ${productId}`);

      const formData = new FormData();
      formData.append('id', productId);
      formData.append('action', 'delete');

      const response = await this.api.post('/products', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      console.log('Delete product response:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('Error deleting product:', error);
      if (error.response) {
        console.error('Server response:', error.response.data);
        throw new Error(`Server error: ${error.response.data.error || 'Unknown server error'}`);
      }
      throw error;
    }
  }

  async deleteAllProducts(): Promise<{ data: { success: boolean; deletedCount: number; message: string } }> {
    try {
      const formData = new FormData();
      formData.append('action', 'delete_all');

      const response = await this.api.post('/products', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      if (!response.data.success) {
        throw new Error(response.data.error || 'Failed to delete all products');
      }

      return response;
    } catch (error: any) {
      console.error('Error deleting all products:', error);
      if (error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw error;
    }
  }

  // Orders Methods
  async getOrders(): Promise<Order[]> {
    try {
      const response = await this.api.get('/orders');
      return response.data || [];
    } catch (error) {
      console.error('Error fetching orders:', error);
      throw error;
    }
  }

  async getOrdersByProductId(productId: string): Promise<Order[]> {
    try {
      const response = await this.api.get(`/orders.php?product_id=${encodeURIComponent(productId)}`);
      return response.data.orders || [];
    } catch (error) {
      console.error(`Error fetching orders for product ${productId}:`, error);
      throw error;
    }
  }

  async getOrdersByUser(userId: string, skip = 0, limit = 10): Promise<{ data: Order[]; total: number }> {
    try {
      const response = await this.api.get(`/orders/${userId}?skip=${skip}&limit=${limit}`);
      // Expecting response.data = { data: Order[], total: number }
      return response.data;
    } catch (error) {
      console.error('Error fetching orders by user:', error);
      throw error;
    }
  }

  async deleteOrder(orderId: string): Promise<void> {
    try {
      const formData = new FormData();
      formData.append('id', orderId);
      formData.append('action', 'delete');

      const response = await this.api.post('/orders', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          ...(token && { Authorization: `Bearer ${token}` })
        }
      });

      console.log('Delete order response:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('Error deleting order:', error);
      if (error.response) {
        console.error('Server response:', error.response.data);
        throw new Error(`Server error: ${error.response.data.error || 'Unknown server error'}`);
      }
      throw error;
    }
  }

  async saveOrder(order: Order): Promise<void> {
    try {
      console.log('Preparing order data:', order);
      const formData = new FormData();
      formData.append('id', order.id);
      formData.append('userId', order.userId);
      formData.append('products', JSON.stringify(order.products));
      formData.append('customerInfo', JSON.stringify(order.customerInfo));
      formData.append('totalAmount', order.totalAmount.toString());
      if (order.diagnosisId) {
        formData.append('diagnosisId', order.diagnosisId);
      }

      // Log the FormData contents
      console.log('FormData contents:');
      for (const [key, value] of formData.entries()) {
        console.log(`${key}:`, value);
      }
      const response = await this.api.post('/orders', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      console.log('Order API response:', response.data);
    } catch (error) {
      console.error('Error saving order:', error);
      if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as { response: { data: any; status: number } };
        console.error('Error response:', axiosError.response.data);
        console.error('Error status:', axiosError.response.status);
      }
      throw error;
    }
  }

  async updateOrderStatus(orderId: string, status: Order['status']): Promise<void> {
    try {
      // First get the current order data to have the old status
      const orders = await this.getOrders();
      const order = orders.find(o => o.id === orderId);

      if (!order) {
        throw new Error('Order not found');
      }

      const oldStatus = order.status;

      // Update the status in the backend
      await this.api.post('/orders', {
        action: 'update_status',
        orderId,
        status
      }, {
        headers: { 'Content-Type': 'application/json' }
      });

      // Update the order object with the new status
      order.status = status;

      // Send email notification if the customer has an email
      if (order.customerInfo.email) {
        try {
          // Import EmailService dynamically to avoid circular dependencies
          const EmailServiceModule = await import('./EmailService');
          const EmailService = EmailServiceModule.default;

          // Create an instance of EmailService
          const emailService = new EmailService();

          // Send the email notification with forceSend=true to ensure it's sent
          await emailService.sendOrderStatusUpdate(order, oldStatus, true);
          console.log('Status update email sent successfully to:', order.customerInfo.email);
        } catch (error) {
          console.error('Failed to send status update email:', error);
          // Don't throw the error to prevent disrupting the order status update
        }
      }
    } catch (error) {
      console.error('Error updating order status:', error);
      throw error;
    }
  }

  // Product Assignment Methods
  async getProductAssignments(): Promise<ProductAssignment[]> {
    try {
      const { data } = await this.api.get('/assignments');
      return data.assignments || [];
    } catch (error) {
      console.error('Error fetching product assignments:', error);
      return [];
    }
  }

  async saveProductAssignment(assignment: ProductAssignment): Promise<void> {
    try {
      console.log('Starting product assignment with:', assignment);

      // First, ensure the diagnosis exists
      const diagnoses = await this.getDiagnoses();
      const diagnosisExists = diagnoses.some(d => d.id === assignment.diagnosisId);

      if (!diagnosisExists) {
        console.log('Diagnosis not found, creating new diagnosis');
        await this.saveDiagnosis({
          id: assignment.diagnosisId,
          name: assignment.optionText,
          description: `Diagnosis for ${assignment.optionText}`
        });
      }

      // Get all products to validate IDs
      const products = await this.getProducts();

      // Create or get products with unique IDs
      const productPromises = assignment.productIds.map(async (productId) => {
        // First check if a product with this ID exists
        const existingProductById = products.find(p => p.id === productId);
        if (existingProductById) {
          console.log(`Found existing product by ID: ${productId}`);
          return productId;
        }

        // If not found by ID, check if a product with this name exists
        const productName = productId; // In some cases, the ID might actually be a name
        const existingProductByName = products.find(p => p.name === productName);
        if (existingProductByName) {
          console.log(`Found existing product by name: ${existingProductByName.id} for name: ${productName}`);
          return existingProductByName.id;
        }

        // Create a new product with a unique ID
        const newProductId = `prod_${Date.now().toString(36)}_${Math.random().toString(36).substring(2, 6)}`;
        console.log(`Creating new product with ID ${newProductId} for name/id: ${productId}`);

        const newProduct = {
          id: newProductId,
          name: productName,
          description: `Product for ${productName}`,
          price: 0,
          inStock: true,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        };

        await this.saveProduct(newProduct);
        console.log(`Successfully created product: ${newProductId}`);
        return newProductId;
      });

      const validatedProductIds = await Promise.all(productPromises);
      console.log('Validated product IDs:', validatedProductIds);

      // Get the questions and options from the database
      const { data } = await this.api.get('/questions');
      const questions = data.questions || [];

      // Find the question
      const question = questions.find((q: any) => q.id === assignment.questionId);
      if (!question) {
        throw new Error(`Question ${assignment.questionId} not found`);
      }

      // Find the option in the question's options array
      const option = question.options.find((opt: any) => opt.text === assignment.optionText);
      if (!option) {
        throw new Error(`Option "${assignment.optionText}" not found in question ${assignment.questionId}`);
      }

      // Check if the option ID exists in the database
      let optionId = assignment.optionId;

      // Get all existing options to verify the option ID exists
      const allOptions = questions.flatMap((q: any) => q.options.map((o: any) => ({
        id: o.id || `${q.id}_opt_${o.text.substring(0, 10).replace(/\s+/g, '_').toLowerCase()}`,
        text: o.text,
        questionId: q.id
      })));

      const existingOption = allOptions.find((o: any) => o.id === optionId);

      if (!existingOption) {
        console.warn(`Option ID ${optionId} not found in database. Searching for matching option by text...`);

        // Try to find an option with matching text in the same question
        const matchingOption = allOptions.find((o: any) =>
          o.text === assignment.optionText && o.questionId === assignment.questionId
        );

        if (matchingOption) {
          console.log(`Found matching option with ID ${matchingOption.id}`);
          optionId = matchingOption.id;
        } else {
          console.error('No matching option found in the database. Cannot create assignment.');
          throw new Error(`Option with text "${assignment.optionText}" not found in question ${assignment.questionId}`);
        }
      }

      // Send the assignment with the verified option ID
      console.log('Using verified option ID:', optionId);

      const formattedAssignment = {
        optionId: optionId,
        questionId: assignment.questionId,
        optionText: assignment.optionText,
        productIds: validatedProductIds,
        diagnosisId: assignment.diagnosisId
      };

      console.log('Sending formatted assignment:', formattedAssignment);

      const response = await this.api.post('/assignments', formattedAssignment);

      if (!response.data.success) {
        throw new Error('Failed to save product assignment');
      }
    } catch (error: any) {
      console.error('Error saving product assignment:', error);
      if (error.response?.data) {
        console.error('Server response:', error.response.data);
      }
      throw error;
    }
  }

  // Diagnoses Methods
  async getDiagnoses(): Promise<Diagnosis[]> {
    try {
      const { data } = await this.api.get('/diagnoses');
      return data.diagnoses || [];
    } catch (error) {
      console.error('Error fetching diagnoses:', error);
      return [];
    }
  }

  async saveDiagnosis(diagnosis: Diagnosis): Promise<void> {
    try {
      await this.api.post('/diagnoses', diagnosis);
    } catch (error) {
      console.error('Error saving diagnosis:', error);
      throw error;
    }
  }

  async deleteDiagnosis(diagnosisId: string): Promise<void> {
    try {
      const formData = new FormData();
      formData.append('id', diagnosisId);
      formData.append('action', 'delete');

      await this.api.post('/diagnoses', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
    } catch (error) {
      console.error('Error deleting diagnosis:', error);
      throw error;
    }
  }

  async deleteAllDiagnoses(): Promise<{
    data: {
      success: boolean;
      deletedCount: number;
      ordersAffected: number;
      eventsAffected: number;
      message: string;
    }
  }> {
    try {
      const formData = new FormData();
      formData.append('action', 'delete_all');

      const response = await this.api.post('/diagnoses', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      if (!response.data.success) {
        throw new Error(response.data.error || 'Failed to delete all diagnoses');
      }

      return response;
    } catch (error: any) {
      console.error('Error deleting all diagnoses:', error);
      if (error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw error;
    }
  }

  // Initialize tables
  private initialize() {
    // ... rest of the initialization code ...
  }

  private saveToFile() {
    if (!this.initialized) return;
    // ... rest of the saveToFile code ...
  }

  private async ensureDefaultDiagnosis(): Promise<void> {
    try {
      const diagnoses = await this.getDiagnoses();
      const defaultDiagnosis = diagnoses.find(d => d.id === 'default_diagnosis');

      if (!defaultDiagnosis) {
        console.log('Creating default diagnosis...');
        await this.saveDiagnosis({
          id: 'default_diagnosis',
          name: 'Default Skin Diagnosis',
          description: 'Default diagnosis for skin conditions'
        });
        console.log('Default diagnosis created successfully');
      } else {
        console.log('Default diagnosis already exists');
      }
    } catch (error) {
      console.error('Error ensuring default diagnosis:', error);
      throw error;
    }
  }

  // ============================================================================
  // NEW TREE-AWARE METHODS (Multi-Tree Functionality)
  // These methods support the new multi-tree functionality
  // ============================================================================

  /**
   * Get all available question trees ordered by display_order
   */
  async getQuestionTrees(): Promise<QuestionTree[]> {
    try {
      const response = await this.api.get('/question-trees');
      return response.data.trees || [];
    } catch (error) {
      console.error('Error fetching question trees:', error);
      throw error;
    }
  }

  /**
   * Get all available tree sequences
   */
  async getTreeSequences(): Promise<TreeSequence[]> {
    try {
      const response = await this.api.get('/tree-sequences');
      return response.data.sequences || [];
    } catch (error) {
      console.error('Error fetching tree sequences:', error);
      throw error;
    }
  }

  /**
   * Get default tree sequence (for main user flow)
   */
  async getDefaultTreeSequence(): Promise<TreeSequenceWithDetails> {
    try {
      const sequences = await this.getTreeSequences();
      const defaultSequence = sequences.find(seq => seq.is_default) || sequences[0];
      
      if (!defaultSequence) {
        throw new Error('No tree sequences available');
      }

      const sequenceDetails = await this.getTreeSequenceById(defaultSequence.id);
      return sequenceDetails;
    } catch (error) {
      console.error('Error fetching default tree sequence:', error);
      throw error;
    }
  }

  /**
   * Get specific tree sequence with full tree details
   */
  async getTreeSequenceById(sequenceId: string): Promise<TreeSequenceWithDetails> {
    try {
      const response = await this.api.get(`/tree-sequences/${sequenceId}`);
      return {
        sequence: response.data.sequence,
        trees: response.data.trees || []
      };
    } catch (error) {
      console.error(`Error fetching tree sequence ${sequenceId}:`, error);
      throw error;
    }
  }

  /**
   * Get questions for a specific tree (maintains same format as getQuestions)
   */
  async getQuestionsByTree(treeId: string): Promise<TreeQuestionResponse> {
    try {
      const response = await this.api.get(`/questions/tree/${treeId}`);
      return {
        tree: response.data.tree,
        questions: response.data.questions || []
      };
    } catch (error) {
      console.error(`Error fetching questions for tree ${treeId}:`, error);
      throw error;
    }
  }

  /**
   * Admin method: Duplicate an existing tree
   */
  async duplicateTree(sourceTreeId: string, newTreeName: string, newFieldName: string, description?: string): Promise<any> {
    try {
      const response = await this.api.post('/admin/duplicate-tree', {
        source_tree_id: sourceTreeId,
        new_tree_name: newTreeName,
        new_field_name: newFieldName,
        new_tree_description: description,
        id_suffix: '_copy'
      });
      return response.data;
    } catch (error) {
      console.error('Error duplicating tree:', error);
      throw error;
    }
  }

  /**
   * Admin method: Quick duplicate for testing
   */
  async quickDuplicateTree(sourceTreeId: string): Promise<any> {
    try {
      const response = await this.api.post(`/admin/quick-duplicate/${sourceTreeId}`);
      return response.data;
    } catch (error) {
      console.error('Error quick duplicating tree:', error);
      throw error;
    }
  }

  /**
   * Admin method: Create new tree sequence
   */
  async createTreeSequence(name: string, description: string, treeIds: string[], isDefault: boolean = false): Promise<any> {
    try {
      const response = await this.api.post('/admin/tree-sequences', {
        name,
        description,
        tree_ids: treeIds,
        is_default: isDefault
      });
      return response.data;
    } catch (error) {
      console.error('Error creating tree sequence:', error);
      throw error;
    }
  }

  /**
   * Admin method: Update tree sequence order
   */
  async updateTreeSequenceOrder(sequenceId: string, treeIds: string[]): Promise<any> {
    try {
      const response = await this.api.put(`/admin/tree-sequences/${sequenceId}`, {
        tree_ids: treeIds
      });
      return response.data;
    } catch (error) {
      console.error('Error updating tree sequence order:', error);
      throw error;
    }
  }

  /**
   * Admin method: Get complete tree structure for visualization
   */
  async getTreeStructure(treeId: string): Promise<any> {
    try {
      const response = await this.api.get(`/admin/tree-structure/${treeId}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching tree structure for ${treeId}:`, error);
      throw error;
    }
  }

  /**
   * Admin method: Delete a question tree
   */
  async deleteQuestionTree(treeId: string): Promise<any> {
    try {
      const response = await this.api.delete(`/admin/question-trees/${treeId}`);
      return response.data;
    } catch (error) {
      console.error(`Error deleting tree ${treeId}:`, error);
      throw error;
    }
  }

  /**
   * Create a new question tree
   */
  async createQuestionTree(treeData: {
    id: string;
    name: string;
    field_name: string;
    description?: string;
    estimated_duration?: number;
  }): Promise<any> {
    try {
      const response = await this.api.post('/admin/question-trees', treeData);
      return response.data;
    } catch (error) {
      console.error('Error creating question tree:', error);
      throw error;
    }
  }

  /**
   * Save questions to a specific tree
   */
  async saveQuestionsToTree(treeId: string, questionsData: any): Promise<void> {
    try {
      console.log(`Saving questions to tree ${treeId}:`, questionsData);

      // Add tree_id to each question
      const questionsWithTreeId = questionsData.questions.map((q: any) => ({
        ...q,
        tree_id: treeId
      }));

      const response = await this.api.post(`/admin/questions/tree/${treeId}`, {
        questions: questionsWithTreeId
      });

      console.log(`Successfully saved ${questionsWithTreeId.length} questions to tree ${treeId}`);
      return response.data;
    } catch (error) {
      console.error(`Error saving questions to tree ${treeId}:`, error);
      throw error;
    }
  }

  async deduplicateProducts(productIds: string[], activeAgents: string): Promise<{
    primary_products: Product[];
    similar_groups: Record<string, { products: Product[]; shared_agents: string[] }>;
  }> {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${baseUrl}/questions/deduplicate-products`,
        {
          product_ids: productIds,
          active_agents: activeAgents
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error deduplicating products:', error);
      // Fallback: return all products as primary if deduplication fails
      const products = await this.getProducts();
      const filteredProducts = products.filter(p => productIds.includes(p.id));
      return {
        primary_products: filteredProducts,
        similar_groups: {}
      };
    }
  }
}

export default new DatabaseService();